/**
 * Lab Sportsbook `getMemberBets` — shared types/helpers (Agent Backend-UI parity).
 */

export type MemberBetTime = 'Current' | 'Past'
export type MemberBetType = 'Matched' | 'Unmatched'
export type MemberBetSource = 'sportsbook' | 'premium-bm' | 'sportbook'

/** Skip Lab "All" / empty filter tokens before POST. */
export function omitAllFilter(value: string | null | undefined): string | undefined {
  const v = value?.trim()
  if (!v || v.toLowerCase() === 'all') return undefined
  return v
}

/** Bet List sport stamp — ignore settlement literal "None". */
export function betListSportLabel(raw: string | null | undefined): string {
  const s = String(raw ?? '').trim()
  if (!s || /^all$/i.test(s) || /^none$/i.test(s) || /^null$/i.test(s) || s === '-') return ''
  return s
}

/**
 * Sheet #46 — sport next to SportsBook on Bet List card title.
 * `SportBook - Cricket Bet List` when Sub Game set; else `SportBook Bet List`.
 */
export function sportBookBetListTitle(subGame?: string | null): string {
  const sport = betListSportLabel(subGame)
  return sport ? `SportBook - ${sport} Bet List` : 'SportBook Bet List'
}

/** Event cell: `Cricket · India W v Zimbabwe W` when eventType present. */
export function sportBookBetEventLabel(eventType: string, eventName: string): string {
  const sport = betListSportLabel(eventType)
  const event = String(eventName ?? '').trim()
  if (sport && event) return `${sport} · ${event}`
  return event || sport
}

export type MemberSportBookBet = {
  id: string
  memberId: string
  userName: string
  placedAt: string | null
  matchedAt: string | null
  eventName: string
  eventType: string
  marketId: string
  marketName: string
  selection: string
  side: 'back' | 'lay'
  oddsPlaced: number
  oddsMatched: number
  runs: number | null
  stake: number
  matched: number
  unmatched: number
  status: string
  betWinStatus: string
  ip: string
  currencyType: string
  betType: string
  isBookmaker: boolean
  isLineLadder: boolean
}

/** Lab Profit/Liability cell (Current vs Past). */
export function memberBetProfitLiability(
  bet: MemberSportBookBet,
  time: MemberBetTime,
): number {
  const matched = bet.matched
  const odds = bet.isLineLadder ? bet.oddsPlaced : bet.oddsMatched
  const openPl = (odds - 1) * matched
  if (time !== 'Past') return openPl

  const win = bet.betWinStatus.toUpperCase()
  if (win === 'LOST') {
    return bet.side === 'back' ? bet.stake : openPl
  }
  if (win === 'WON') {
    return bet.side === 'lay' ? bet.stake : openPl
  }
  return 0
}

export function dayStartIso(day: string): string {
  return `${day.slice(0, 10)}T00:00:00.000Z`
}

export function dayEndIso(day: string): string {
  return `${day.slice(0, 10)}T23:59:59.999Z`
}
